/**
 * BudgetOverviewTower - 工程指挥塔风格预算概览内容组件
 *
 * 设计理念：Control Tower（指挥塔）
 * - 浅色主题，匹配 Ant Design 5 浅色基调
 * - Tab 切换：预算概览 / 合同管理 / 调整历史
 * - 等宽数字（JetBrains Mono），精确感
 * - 预警中心 + 快捷操作
 *
 * 用法：
 *   <ProjectOverviewModal visible={...} project={...}>
 *     <BudgetOverviewTower
 *       project={mergedProject}
 *       stats={projectStats}
 *       contractSummary={contractSummary}
 *       onSetupBudget={...}
 *       onViewDetail={...}
 *       onAddRecord={...}
 *     />
 *   </ProjectOverviewModal>
 */
import React, { useState, useEffect, useRef } from 'react';
import { Spin, Tag, Progress, Tooltip } from 'antd';
import {
  UserOutlined, ToolOutlined, GoldOutlined, FileTextOutlined,
  SafetyCertificateOutlined, LockOutlined, UnlockOutlined,
  HistoryOutlined, EditOutlined
} from '@ant-design/icons';
import { Radio } from 'antd';
import type { ProjectOverviewData } from '../../services/dashboardService';
import type { ProjectContractSummary } from '../../services/projectContractService';
import type { ProjectComprehensiveStats } from '../../types/report.types';
import type { BudgetBreakdown, RevenueBreakdown } from '../../types';
import type { RevenueDetailType } from './CostDetailTypes';
import { lockBudget, unlockBudget, getBudgetHistory } from '../../services/budgetService';
import { ProjectContractService } from '../../services/projectContractService';
import BudgetHistoryModal from '../BudgetMonitor/BudgetHistoryModal';
import WorkTimePanel from './DetailRecords/WorkTimePanel';
import EquipmentPanel from './DetailRecords/EquipmentPanel';
import MaterialPanel from './DetailRecords/MaterialPanel';
import SubcontractPanel from './DetailRecords/SubcontractPanel';
import CoordinationPanel from './DetailRecords/CoordinationPanel';
import RevenueDetailPanel from './DetailRecords/RevenueDetailPanel';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';

// ============== CSS TOKENS — 统一风格，与 Dashboard Tower 一致 ==============
const tokens: Record<string, string> = {
  '--bg-page': '#f0f2f5',
  '--bg-card': 'rgba(224, 230, 234, 0.82)',
  '--bg-card-hover': 'rgba(214, 221, 226, 0.92)',
  '--bg-surface': 'rgba(210, 218, 224, 0.9)',
  '--bg-control': 'rgba(236, 240, 243, 0.88)',
  '--bg-control-strong': 'rgba(228, 233, 237, 0.94)',
  '--text-primary': '#10171d',
  '--text-secondary': '#455763',
  '--text-muted': '#667986',
  '--border': 'rgba(16, 27, 35, 0.08)',
  '--border-hover': 'rgba(16, 27, 35, 0.16)',
  '--accent-gold': '#b99258',
  '--accent-gold-soft': 'rgba(185, 146, 88, 0.14)',
  '--accent-blue': '#547590',
  '--accent-blue-soft': 'rgba(84, 117, 144, 0.12)',
  '--accent-mint': '#5d8a7f',
  '--accent-mint-soft': 'rgba(101, 214, 184, 0.16)',
  '--accent-red': '#c46d5f',
  '--accent-red-soft': 'rgba(196, 109, 95, 0.14)',
  '--accent-amber': '#d9a86b',
  '--accent-amber-soft': 'rgba(246, 196, 83, 0.16)',
  '--accent-purple': '#9b8dc9',
  '--accent-purple-soft': 'rgba(155, 141, 201, 0.16)',
  '--shadow': '0 28px 60px rgba(46, 60, 70, 0.14)',
  '--shadow-sm': '0 8px 24px rgba(46, 60, 70, 0.1)',
  '--radius-lg': '16px',
  '--radius-md': '12px',
  '--radius-sm': '8px',
};

// ============== UTILITIES ==============
const fmt = (n: number, compact = false): string => {
  if (compact && Math.abs(n) >= 10000) return `¥${(n / 10000).toFixed(1)}万`;
  return `¥${n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
};

// ============== TYPES ==============
type TabKey = 'overview' | 'contract' | 'history';
type CostDetailType = 'worktime' | 'equipment' | 'material' | 'subcontract' | 'coordination';

interface BudgetOverviewTowerProps {
  project: ProjectOverviewData & {
    budgetBreakdown?: BudgetBreakdown;
    revenueTotal?: number;
    revenueBreakdown?: RevenueBreakdown;
    budgetVersion?: number;
    budgetLocked?: boolean;
    budgetLockedAt?: string;
    budgetLockedBy?: { username: string; realName?: string };
  };
  stats?: ProjectComprehensiveStats;
  contractSummary?: ProjectContractSummary | null;
  constructionType?: string;
  onConstructionTypeChange?: (type: string | undefined) => void;
  onBudgetRefresh?: () => void;
  onEditProject?: () => void;
  onViewRevenueDetail?: (type: RevenueDetailType) => void;
  onAddRecord?: (type: CostDetailType) => void;
  onAddAddition?: () => void;
  onAddRectification?: () => void;
  onSetupBudget?: () => void;
}

// ============== BUDGET HEALTH CALCULATION ==============
interface BudgetHealth {
  status: 'unset' | 'healthy' | 'good' | 'warning' | 'danger' | 'over';
  label: string;
  color: string;
  percentage: number;
  message: string;
}

function calculateBudgetHealth(budget: number, actualCost: number): BudgetHealth {
  if (!budget || budget === 0) {
    return { status: 'unset', label: '未配置', color: '#9ca8b3', percentage: 0, message: '请设置项目预算以启用成本监控' };
  }
  const percentage = (actualCost / budget) * 100;
  const remaining = budget - actualCost;
  if (percentage <= 50) {
    return { status: 'healthy', label: '健康', color: '#10b981', percentage, message: `预算充足，剩余 ¥${Math.abs(remaining).toLocaleString()}` };
  } else if (percentage <= 70) {
    return { status: 'good', label: '良好', color: '#3b82f6', percentage, message: '预算使用正常，请继续保持' };
  } else if (percentage <= 90) {
    return { status: 'warning', label: '注意', color: '#f59e0b', percentage, message: '预算使用较多，请注意成本控制' };
  } else if (percentage <= 100) {
    return { status: 'danger', label: '紧张', color: '#ef4444', percentage, message: '预算即将用尽，请严格控制支出' };
  }
  return { status: 'over', label: '超支', color: '#cf1322', percentage, message: `已超支 ¥${Math.abs(remaining).toLocaleString()}` };
}

// ============== STATUS BADGE ==============
function StatusBadge({ status }: { status: BudgetHealth['status'] }) {
  const configs: Record<BudgetHealth['status'], { label: string; color: string; bg: string; border: string }> = {
    unset:   { label: '未配置', color: '#667986', bg: 'rgba(102, 121, 134, 0.1)', border: 'rgba(102, 121, 134, 0.2)' },
    healthy: { label: '健康', color: '#5d8a7f', bg: 'rgba(101, 214, 184, 0.14)', border: 'rgba(93, 138, 127, 0.2)' },
    good:    { label: '良好', color: '#547590', bg: 'rgba(84, 117, 144, 0.12)', border: 'rgba(84, 117, 144, 0.2)' },
    warning: { label: '注意', color: '#d9a86b', bg: 'rgba(246, 196, 83, 0.14)', border: 'rgba(217, 168, 107, 0.2)' },
    danger:  { label: '紧张', color: '#c46d5f', bg: 'rgba(196, 109, 95, 0.14)', border: 'rgba(196, 109, 95, 0.2)' },
    over:    { label: '超支', color: '#c46d5f', bg: 'rgba(196, 109, 95, 0.14)', border: 'rgba(196, 109, 95, 0.2)' },
  };
  const c = configs[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      color: c.color, background: c.bg, border: `1px solid ${c.border}`,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: c.color,
        boxShadow: status !== 'unset' ? `0 0 6px ${c.color}66` : 'none',
      }} />
      {c.label}
    </span>
  );
}

// ============== HERO METRIC CARD ==============
function HeroCard({
  label, value, variant = 'default', trend, trendUp, onClick, cta,
}: {
  label: string;
  value: number;
  variant?: 'cost' | 'revenue' | 'profit' | 'budget' | 'default';
  trend?: number;
  trendUp?: boolean;
  onClick?: () => void;
  cta?: string;
}) {
  const colors: Record<string, string> = {
    cost: '#547590', revenue: '#5d8a7f', profit: '#b99258',
    budget: '#667986', default: '#10171d',
  };
  const color = colors[variant];
  const isEmpty = variant === 'budget' && value === 0;

  const cardStyle: React.CSSProperties = {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '14px 16px',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.2s', position: 'relative', overflow: 'hidden',
    minHeight: 86,
    boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column',
  };

  return (
    <div style={cardStyle}
      onClick={onClick}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-card-hover)';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-hover)';
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-sm)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-card)';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
        (e.currentTarget as HTMLElement).style.transform = 'none';
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
      }}
    >
      {!isEmpty && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: color, borderRadius: '12px 12px 0 0',
        }} />
      )}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 8, letterSpacing: '0.02em' }}>
        {label}
      </div>
      {isEmpty ? (
        <div
          onClick={e => { e.stopPropagation(); onClick?.(); }}
          style={{
            fontSize: 11, color: 'var(--accent-blue)', fontWeight: 600,
            border: '1px dashed var(--border)', borderRadius: 6,
            padding: '6px 10px', display: 'inline-block',
            background: 'repeating-linear-gradient(-45deg, var(--bg-card), var(--bg-card) 8px, rgba(0,0,0,0.015) 8px, rgba(0,0,0,0.015) 16px)',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-blue)';
            (e.currentTarget as HTMLElement).style.color = '#3d6580';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
            (e.currentTarget as HTMLElement).style.color = 'var(--accent-blue)';
          }}
        >
          {cta || '配置 →'}
        </div>
      ) : (
        <div style={{
          fontSize: 20, fontWeight: 700,
          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
          color, lineHeight: 1, marginBottom: 6,
        }}>
          {fmt(value)}
        </div>
      )}
      {trend !== undefined && !isEmpty && (
        <div style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 3, color: trendUp ? 'var(--accent-mint)' : 'var(--accent-red)' }}>
          <span>{trendUp ? '↑' : '↓'}</span>
          <span style={{ fontFamily: '"JetBrains Mono", monospace' }}>{Math.abs(trend).toFixed(1)}%</span>
          <span style={{ color: 'var(--text-muted)', marginLeft: 2 }}>vs上月</span>
        </div>
      )}
    </div>
  );
}

function SelfOperatedMetricCard({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div style={{
      padding: '10px 12px',
      borderRadius: 'var(--radius-sm)',
      background: highlight ? 'var(--accent-blue-soft)' : 'var(--bg-surface)',
      border: `1px solid ${highlight ? 'rgba(84, 117, 144, 0.2)' : 'var(--border)'}`,
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace' }}>
        {value}
      </div>
    </div>
  );
}

function SelfOperatedSummaryPanel({ summary }: { summary: NonNullable<ProjectComprehensiveStats['selfOperatedSummary']> }) {
  const overview = summary.overview;

  const formatPercent = (value: number | null) => value === null ? '—' : `${value.toFixed(2)}%`;

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      padding: 16,
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12, alignItems: 'start' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em', marginBottom: 4 }}>
            自有施工进度与成本
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {summary.caliberNote}
          </div>
        </div>
        <Tag color="blue">项目口径</Tag>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
        <SelfOperatedMetricCard label="目标产值" value={fmt(overview.targetOutput)} />
        <SelfOperatedMetricCard label="累计产值" value={fmt(overview.currentOutput)} />
        <SelfOperatedMetricCard label="产值完成率" value={formatPercent(overview.progressRate)} highlight />
        <SelfOperatedMetricCard label="自有施工成本" value={fmt(overview.actualDirectCost)} />
        <SelfOperatedMetricCard label="产值成本差" value={fmt(overview.outputCostGap)} />
        <SelfOperatedMetricCard label="成本产值比" value={formatPercent(overview.costOutputRatio)} />
      </div>

      <div style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '88px repeat(5, 1fr)',
          gap: 8,
          padding: '10px 12px',
          background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--border)',
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--text-secondary)',
        }}>
          <span>施工类型</span>
          <span>目标产值</span>
          <span>累计产值</span>
          <span>完成率</span>
          <span>直接成本</span>
          <span>成本产值比</span>
        </div>
        {summary.byConstructionType.map((item) => (
          <div
            key={item.constructionType}
            style={{
              display: 'grid',
              gridTemplateColumns: '88px repeat(5, 1fr)',
              gap: 8,
              padding: '10px 12px',
              borderBottom: '1px solid var(--border)',
              fontSize: 12,
              color: 'var(--text-primary)',
            }}
          >
            <span style={{ fontWeight: 700 }}>{item.constructionType}</span>
            <span>{fmt(item.targetOutput, true)}</span>
            <span>{fmt(item.currentOutput, true)}</span>
            <span>{formatPercent(item.progressRate)}</span>
            <span>{fmt(item.actualDirectCost, true)}</span>
            <span>{formatPercent(item.costOutputRatio)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== EXECUTION RING ==============
function ExecutionRing({ budget, actualCost, breakdown }: {
  budget: number;
  actualCost: number;
  breakdown: { labor: number; material: number; equipment: number; subcontract: number; coordination: number };
}) {
  const health = calculateBudgetHealth(budget, actualCost);
  const totalActual = breakdown.labor + breakdown.material + breakdown.equipment + breakdown.subcontract + breakdown.coordination;

  const categories = [
    { key: 'labor', label: '人工', color: '#547590', budgetKey: 'labor' as keyof typeof breakdown, cost: breakdown.labor },
    { key: 'material', label: '材料', color: '#d9a86b', budgetKey: 'material' as keyof typeof breakdown, cost: breakdown.material },
    { key: 'subcontract', label: '分包', color: '#9b8dc9', budgetKey: 'subcontract' as keyof typeof breakdown, cost: breakdown.subcontract },
    { key: 'equipment', label: '设备', color: '#5d8a7f', budgetKey: 'equipment' as keyof typeof breakdown, cost: breakdown.equipment },
    { key: 'coordination', label: '协调', color: '#b99258', budgetKey: 'coordination' as keyof typeof breakdown, cost: breakdown.coordination },
  ];

  const r = 58;
  const circ = 2 * Math.PI * r;
  const effectiveRate = Math.min(health.percentage, 100);
  const dashOffset = circ * (1 - effectiveRate / 100);
  const isEmpty = budget === 0 || actualCost === 0;

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* Ring */}
      <div style={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
        <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="70" cy="70" r={r} fill="none" stroke="var(--border)" strokeWidth="9" />
          {isEmpty ? (
            <circle cx="70" cy="70" r={r} fill="none" stroke="var(--text-muted)" strokeWidth="9" strokeDasharray="4 4" strokeLinecap="round" opacity="0.3" />
          ) : (
            <circle cx="70" cy="70" r={r} fill="none" stroke={health.color} strokeWidth="9" strokeDasharray={circ} strokeDashoffset={dashOffset} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
          )}
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {isEmpty ? (
            <>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>—</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>无预算</span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 24, fontWeight: 700, color: health.color, fontFamily: '"JetBrains Mono", monospace' }}>
                {health.percentage.toFixed(0)}%
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>执行率</span>
            </>
          )}
        </div>
      </div>

      {/* Category bars */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {categories.map(cat => {
          const catBudget = budget > 0 ? budget / 5 : 0;
          const catRate = catBudget > 0 ? (cat.cost / catBudget * 100) : 0;
          return (
            <div key={cat.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 2, background: cat.color }} />
                  {cat.label}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>
                  {fmt(cat.cost, true)}
                  {budget > 0 && (
                    <span style={{ marginLeft: 4, color: 'var(--text-muted)' }}>/ {catRate.toFixed(0)}%</span>
                  )}
                </span>
              </div>
              <div style={{ height: 5, background: 'var(--bg-surface)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${Math.min(catRate, 100)}%`, borderRadius: 3,
                  background: cat.color, opacity: 0.75,
                  transition: 'width 1s ease-out',
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============== REVENUE RING ==============
function RevenueRing({ revenue, total }: {
  revenue: RevenueBreakdown;
  total: number;
}) {
  const items = [
    { key: 'contract', label: '合同', value: revenue.contract, color: '#9b8dc9' },
    { key: 'changeOrder', label: '增项', value: revenue.changeOrder, color: '#5d8a7f' },
    { key: 'claim', label: '签证', value: revenue.claim, color: '#b99258' },
    { key: 'settlement', label: '结算', value: revenue.settlement, color: '#d9a86b' },
    { key: 'other', label: '其他', value: revenue.other, color: '#667986' },
  ].filter(i => i.value > 0);

  const isEmpty = total === 0;
  const r = 58;
  const circ = 2 * Math.PI * r;

  const getArcPaths = () => {
    if (isEmpty || total === 0) return [];
    let offset = 0;
    return items.map(item => {
      const pct = item.value / total;
      const len = circ * pct;
      const path = {
        item,
        offset,
        len,
        dashArray: `${len} ${circ - len}`,
        dashOffset: circ * (1 - offset / circ),
      };
      offset += len;
      return path;
    });
  };

  const arcPaths = getArcPaths();
  const circumference = circ;

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* Ring */}
      <div style={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
        <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="70" cy="70" r={r} fill="none" stroke="var(--border)" strokeWidth="9" />
          {isEmpty ? (
            <circle cx="70" cy="70" r={r} fill="none" stroke="var(--text-muted)" strokeWidth="9" strokeDasharray="4 4" strokeLinecap="round" opacity="0.3" />
          ) : (
            arcPaths.map((ap, i) => (
              <circle
                key={ap.item.key}
                cx="70" cy="70" r={r}
                fill="none"
                stroke={ap.item.color}
                strokeWidth="9"
                strokeDasharray={`${ap.len} ${circ - ap.len}`}
                strokeDashoffset={-circ * (items.slice(0, i).reduce((s, it) => s + it.value, 0) / total)}
                strokeLinecap="butt"
                opacity="0.9"
              />
            ))
          )}
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {isEmpty ? (
            <>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>—</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>无收入</span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 22, fontWeight: 700, color: '#9b8dc9', fontFamily: '"JetBrains Mono", monospace' }}>
                {items.length}
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>项来源</span>
            </>
          )}
        </div>
      </div>

      {/* Category bars */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {items.length > 0 ? items.map(item => {
          const pct = total > 0 ? (item.value / total) * 100 : 0;
          return (
            <div key={item.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 2, background: item.color }} />
                  {item.label}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>
                  {fmt(item.value, true)}
                  <span style={{ marginLeft: 4, color: 'var(--text-muted)' }}>/ {pct.toFixed(1)}%</span>
                </span>
              </div>
              <div style={{ height: 5, background: 'var(--bg-surface)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${pct}%`, borderRadius: 3,
                  background: item.color, opacity: 0.75,
                  transition: 'width 1s ease-out',
                }} />
              </div>
            </div>
          );
        }) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {['合同', '增项', '签证', '结算', '其他'].map(label => (
              <div key={label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 2, background: 'var(--border)' }} />
                    {label}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>— / —</span>
                </div>
                <div style={{ height: 5, background: 'var(--bg-surface)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: '0%', borderRadius: 3, background: 'var(--border)' }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============== REVENUE LIST ==============
function RevenueList({ revenue, total, onItemClick }: {
  revenue: RevenueBreakdown;
  total: number;
  onItemClick?: (category: string) => void;
}) {
  const catColors: Record<string, string> = {
    contract: '#9b8dc9', changeOrder: '#5d8a7f', claim: '#b99258',
    settlement: '#d9a86b', other: '#667986',
  };
  const catLabels: Record<string, string> = {
    contract: '合同收入', changeOrder: '增项收入', claim: '签证收入',
    settlement: '结算收入', other: '其他收入',
  };

  const items = Object.entries(revenue)
    .map(([key, value]) => ({
      key,
      name: catLabels[key] || key,
      amount: value,
      pct: total > 0 ? (value / total) * 100 : 0,
      category: key,
    }))
    .filter(i => i.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  return (
    <div>
      {items.map((item, i) => (
        <div key={item.key} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 8px', borderRadius: 8,
          cursor: onItemClick ? 'pointer' : 'default', transition: 'background 0.15s',
        }}
          onMouseEnter={e => ((e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)')}
          onMouseLeave={e => ((e.currentTarget as HTMLElement).style.background = 'transparent')}
          onClick={() => onItemClick?.(item.key)}
        >
          <div style={{
            width: 18, height: 18, borderRadius: 4, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace',
            background: `${catColors[item.category]}18`, color: catColors[item.category],
          }}>
            {i + 1}
          </div>
          <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)' }}>{item.name}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 50, height: 4, background: 'var(--bg-surface)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${item.pct}%`, background: catColors[item.category], borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace', minWidth: 60, textAlign: 'right', color: 'var(--text-primary)' }}>
              {fmt(item.amount, true)}
            </span>
            <span style={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace', minWidth: 36, textAlign: 'right', color: 'var(--text-muted)' }}>
              {item.pct.toFixed(1)}%
            </span>
          </div>
        </div>
      ))}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>收入合计</span>
        <span style={{ fontSize: 14, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: '#9b8dc9' }}>{fmt(total)}</span>
      </div>
    </div>
  );
}

// ============== REVENUE SECTION ==============
function RevenueSection({ revenue, total, contractSummary, projectId, onItemClick }: {
  revenue: RevenueBreakdown;
  total: number;
  contractSummary?: ProjectContractSummary | null;
  projectId?: string;
  onItemClick?: (category: string) => void;
}) {
  return (
    <div>
      <RevenueRing revenue={revenue} total={total} />

      {contractSummary && contractSummary.contractCount > 0 && (
        <div style={{
          marginTop: 12, padding: '10px 12px',
          background: 'var(--accent-purple-soft)', borderRadius: 'var(--radius-sm)',
          border: '1px solid rgba(155, 141, 201, 0.16)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>合同管理（独立模块）</span>
            <button
              onClick={() => window.location.href = `/project-contract-management?projectId=${projectId}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--accent-purple)', fontWeight: 600 }}
            >
              查看 {contractSummary.contractCount} 份 →
            </button>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: 'var(--accent-purple)' }}>
            {fmt(contractSummary.totalAmount, true)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <div style={{ flex: 1, height: 4, background: 'var(--accent-purple-soft)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${contractSummary.paymentProgress}%`, background: 'var(--accent-purple)', borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>
              付款 {contractSummary.paymentProgress.toFixed(1)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== COST LIST ==============
function CostList({ items, expandedType, projectId, isMobile, onToggle, onClose, constructionType, onConstructionTypeChange }: {
  items: Array<{ name: string; amount: number; pct: number; category: string }>;
  expandedType?: CostDetailType | null;
  projectId?: string;
  isMobile?: boolean;
  onToggle?: (type: CostDetailType) => void;
  onClose?: () => void;
  constructionType?: string;
  onConstructionTypeChange?: (type: string | undefined) => void;
}) {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (expandedType) {
      const idx = items.findIndex(item => catMap[item.category] === expandedType);
      const el = itemRefs.current[idx];
      if (el) {
        const container = el.closest('.ant-modal-body') as HTMLElement | null;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const visibleHeight = containerRect.height;
          const elBottomInViewport = elRect.bottom - containerRect.top;
          const neededScroll = elBottomInViewport - visibleHeight + 16;
          if (neededScroll > 0) {
            container.scrollTo({ top: container.scrollTop + neededScroll, behavior: 'smooth' });
          }
        }
      }
    }
  }, [expandedType]);
  const catColors: Record<string, string> = {
    labor: '#3b82f6', material: '#f59e0b', equipment: '#10b981',
    subcontract: '#8b5cf6', coordination: '#06b6d4',
  };
  const catMap: Record<string, CostDetailType> = {
    labor: 'worktime', material: 'material', equipment: 'equipment',
    subcontract: 'subcontract', coordination: 'coordination',
  };
  const catBgColors: Record<string, string> = {
    labor: 'rgba(59,130,246,0.08)', material: 'rgba(245,158,11,0.08)', equipment: 'rgba(16,185,129,0.08)',
    subcontract: 'rgba(139,92,246,0.08)', coordination: 'rgba(6,182,212,0.08)',
  };
  const total = items.reduce((s, i) => s + i.amount, 0);

  const renderPanelContent = () => {
    if (!projectId || !expandedType) return null;
    const innerPanelStyle: React.CSSProperties = {
      background: 'var(--bg-surface)',
      border: `1px solid ${catColors[Object.keys(catMap).find(k => catMap[k] === expandedType) || ''] || 'var(--border)'}30`,
      borderRadius: 'var(--radius-sm)',
      marginTop: 4,
      marginBottom: 4,
      overflow: 'hidden',
    };
    const innerContentStyle: React.CSSProperties = {
      overflowY: 'auto',
    };

    let panelContent: React.ReactNode = null;
    switch (expandedType) {
      case 'worktime':
        panelContent = <WorkTimePanel projectId={projectId} active isMobile={isMobile || false} constructionType={constructionType} />;
        break;
      case 'equipment':
        panelContent = <EquipmentPanel projectId={projectId} active isMobile={isMobile || false} constructionType={constructionType} />;
        break;
      case 'material':
        panelContent = <MaterialPanel projectId={projectId} active isMobile={isMobile || false} constructionType={constructionType} />;
        break;
      case 'subcontract':
        panelContent = <SubcontractPanel projectId={projectId} active isMobile={isMobile || false} constructionType={constructionType} />;
        break;
      case 'coordination':
        panelContent = <CoordinationPanel projectId={projectId} active isMobile={isMobile || false} constructionType={constructionType} />;
        break;
    }

    return (
      <div style={innerPanelStyle}>
        <div style={innerContentStyle}>
          {panelContent}
        </div>
      </div>
    );
  };

  return (
    <div>
      {items.map((item, i) => {
        const type = catMap[item.category];
        const isExpanded = type === expandedType;
        return (
          <div key={i} ref={el => { itemRefs.current[i] = el; }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 8px', borderRadius: 8,
              cursor: 'pointer', transition: 'all 0.18s',
              background: isExpanded ? catBgColors[item.category] : undefined,
              border: isExpanded ? `1px solid ${catColors[item.category]}30` : '1px solid transparent',
            }}
              onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.03)'; }}
              onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              onClick={() => type && onToggle?.(type)}
            >
              <div style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace',
                background: `${catColors[item.category]}18`, color: catColors[item.category],
              }}>
                {i + 1}
              </div>
              <span style={{ flex: 1, fontSize: 12, color: isExpanded ? '#1a2129' : '#5c6b78', fontWeight: isExpanded ? 600 : 400 }}>{item.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 50, height: 4, background: 'rgba(0,0,0,0.05)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${item.pct}%`, background: catColors[item.category], borderRadius: 2 }} />
                </div>
                <span style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace', minWidth: 60, textAlign: 'right', color: '#1a2129' }}>
                  {fmt(item.amount, true)}
                </span>
                <span style={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace', minWidth: 36, textAlign: 'right', color: '#9ca8b3' }}>
                  {item.pct.toFixed(1)}%
                </span>
                <span style={{
                  fontSize: 10, color: isExpanded ? catColors[item.category] : '#9ca8b3',
                  transition: 'transform 0.25s, color 0.15s',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'inline-block', width: 14, textAlign: 'center',
                }}>
                  ↓
                </span>
              </div>
            </div>
            {/* 展开面板内嵌在点击项下方，仅当此项展开时显示 */}
            {isExpanded && renderPanelContent()}
          </div>
        );
      })}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: '#9ca8b3' }}>
            成本合计{constructionType ? `（${constructionType}）` : ''}
          </span>
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace' }}>{fmt(total)}</span>
      </div>
    </div>
  );
}

// ============== ROLLER BLIND PANEL ==============
function RollerBlindPanel({ type, projectId, isMobile, onClose }: {
  type: CostDetailType;
  projectId?: string;
  isMobile: boolean;
  onClose?: () => void;
}) {
  const panelConfig: Record<CostDetailType, { title: string }> = {
    worktime: { title: '人工成本明细' },
    equipment: { title: '机械成本明细' },
    material: { title: '材料成本明细' },
    subcontract: { title: '分包成本明细' },
    coordination: { title: '协调成本明细' },
  };

  const renderContent = () => {
    if (!projectId) return null;
    switch (type) {
      case 'worktime':
        return <WorkTimePanel projectId={projectId} active isMobile={isMobile} />;
      case 'equipment':
        return <EquipmentPanel projectId={projectId} active isMobile={isMobile} />;
      case 'material':
        return <MaterialPanel projectId={projectId} active isMobile={isMobile} />;
      case 'subcontract':
        return <SubcontractPanel projectId={projectId} active isMobile={isMobile} />;
      case 'coordination':
        return <CoordinationPanel projectId={projectId} active isMobile={isMobile} />;
      default:
        return null;
    }
  };

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 16px',
        background: 'var(--bg-control)',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            {panelConfig[type]?.title}
          </span>
          <span style={{
            fontSize: 10, fontWeight: 600,
            background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)',
            padding: '1px 6px', borderRadius: 4,
          }}>
            卷帘展开
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)',
            padding: '4px 10px', borderRadius: 6,
            transition: 'all 0.15s',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.color = 'var(--text-primary)';
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-hover)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
          }}
        >
          收起  ↑
        </button>
      </div>
      <div style={{ maxHeight: 480, overflowY: 'auto' }}>
        {renderContent()}
      </div>
    </div>
  );
}

// ============== ALERT SECTION ==============
function AlertSection({ alerts, onAlertAction }: {
  alerts: Array<{ id: string; type: 'danger' | 'warning' | 'info' | 'success'; title: string; desc: string; action?: string }>;
  onAlertAction?: (id: string) => void;
}) {
  const getStyle = (type: string) => {
    const map: Record<string, { bar: string; bg: string; border: string; color: string }> = {
      danger:  { bar: 'var(--accent-red)', bg: 'var(--accent-red-soft)', border: 'rgba(196,109,95,0.15)', color: 'var(--accent-red)' },
      warning: { bar: 'var(--accent-amber)', bg: 'var(--accent-amber-soft)', border: 'rgba(246,196,83,0.15)', color: 'var(--accent-amber)' },
      info:    { bar: 'var(--accent-blue)', bg: 'var(--accent-blue-soft)', border: 'rgba(84,117,144,0.12)', color: 'var(--accent-blue)' },
      success: { bar: 'var(--accent-mint)', bg: 'var(--accent-mint-soft)', border: 'rgba(101,214,184,0.12)', color: 'var(--accent-mint)' },
    };
    return map[type] || map.info;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {alerts.map(alert => {
        const s = getStyle(alert.type);
        return (
          <div key={alert.id} style={{
            display: 'flex', gap: 10, padding: '10px 12px',
            background: s.bg, borderRadius: 'var(--radius-sm)', border: `1px solid ${s.border}`,
          }}>
            <div style={{ width: 3, borderRadius: 2, background: s.bar, flexShrink: 0, alignSelf: 'stretch' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{alert.title}</span>
                {alert.action && (
                  <button
                    onClick={() => onAlertAction?.(alert.id)}
                    style={{
                      background: 'none', border: `1px solid ${s.color}50`, cursor: 'pointer',
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, color: s.color,
                    }}
                  >
                    {alert.action}
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.5 }}>{alert.desc}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============== CONTRACT TAB ==============
function ContractTab({ contracts }: { contracts: any[] }) {
  const totalAmount = contracts.reduce((s: number, c: any) => s + c.contractAmount, 0);
  const totalPaid = contracts.reduce((s: number, c: any) => s + (c.paidAmount || 0), 0);
  const paymentPct = totalAmount > 0 ? (totalPaid / totalAmount * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>📋 项目合同列表</span>
          <button style={{
            background: 'var(--accent-blue)', color: '#fff',
            border: 'none',
            padding: '5px 12px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 500, cursor: 'pointer',
          }}>
            + 新增合同
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {contracts.map((c: any) => (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              background: 'var(--bg-control)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.borderColor = 'var(--border-hover)')}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.borderColor = 'var(--border)')}
            >
              <div style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0, background: 'var(--accent-purple-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📄</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.contractName || c.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {c.contractNo ? `合同编号：${c.contractNo}` : ''}
                  {c.signDate ? ` · ${c.signDate}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-purple)', fontFamily: '"JetBrains Mono", monospace' }}>
                  {fmt(c.contractAmount || c.amount, true)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  已付 {((c.paidAmount || 0) / (c.contractAmount || c.amount || 1) * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {[
          { label: '合同总额', value: fmt(totalAmount, true), color: 'var(--accent-purple)' },
          { label: '累计付款', value: fmt(totalPaid, true), color: 'var(--accent-blue)' },
          { label: '付款进度', value: `${paymentPct.toFixed(1)}%` },
          { label: '合同数量', value: `${contracts.length} 份` },
          { label: '待付款', value: fmt(totalAmount - totalPaid, true), color: 'var(--accent-amber)' },
          { label: '平均进度', value: `${paymentPct.toFixed(1)}%` },
        ].map((item, i) => (
          <div key={i} style={{ padding: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{item.label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: item.color || 'var(--text-primary)', fontFamily: item.color ? '"JetBrains Mono", monospace' : 'inherit' }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== HISTORY TAB ==============
function HistoryTab({ history }: { history: any[] }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>📜 预算调整历史</span>
        <button style={{
          background: 'var(--bg-control)', color: 'var(--text-secondary)', border: '1px solid var(--border)',
          padding: '5px 12px', borderRadius: 'var(--radius-sm)', fontSize: 12, cursor: 'pointer',
        }}>
          导出
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '80px 100px 1fr 100px', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
        <span>版本</span><span>日期</span><span>调整说明</span><span>操作人</span>
      </div>
      {history.map((h: any, i: number) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '80px 100px 1fr 100px', gap: 8,
          padding: '10px 12px', borderBottom: '1px solid var(--border)',
          alignItems: 'center',
          transition: 'background 0.15s', cursor: 'default',
        }}
          onMouseEnter={e => ((e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)')}
          onMouseLeave={e => ((e.currentTarget as HTMLElement).style.background = 'transparent')}
        >
          <span style={{ fontWeight: 700, color: h.color || 'var(--accent-blue)', fontFamily: '"JetBrains Mono", monospace' }}>{h.version}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{h.date || h.createdAt?.split('T')[0]}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{h.description || h.changeDescription || h.note || '—'}</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{h.operatorName || h.operator?.username || h.createdBy?.username || '—'}</span>
        </div>
      ))}
    </div>
  );
}

// ============== MAIN COMPONENT ==============
const BudgetOverviewTower: React.FC<BudgetOverviewTowerProps> = ({
  project,
  stats,
  contractSummary,
  constructionType,
  onConstructionTypeChange,
  onBudgetRefresh,
  onEditProject,
  onViewRevenueDetail,
  onAddRecord,
  onAddAddition,
  onAddRectification,
  onSetupBudget,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [expandedCostType, setExpandedCostType] = useState<CostDetailType | null>(null);
  const [expandedRevenueType, setExpandedRevenueType] = useState<RevenueDetailType | null>(null);
  const [lockLoading, setLockLoading] = useState(false);
  const { isMobile } = useResponsiveConfig();
  const [unlockModalVisible, setUnlockModalVisible] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [contractList, setContractList] = useState<any[]>([]);
  const [budgetHistory, setBudgetHistory] = useState<any[]>([]);

  // Load contract list and budget history
  useEffect(() => {
    if (!project?.id) return;
    ProjectContractService.getContracts({ projectId: project.id, limit: 10 })
      .then((res: any) => { if (res.success && Array.isArray(res.data)) setContractList(res.data); })
      .catch(console.error);

    getBudgetHistory(project.id, { page: 1, limit: 20 })
      .then((res: any) => {
        if (res.success && res.data?.items) setBudgetHistory(res.data.items);
      }).catch(console.error);
  }, [project?.id]);

  // 优先使用 stats.summary.totalProjectCost（与 Dashboard 口径一致），兜底使用各成本项求和
  // 注意：协调费用没有 constructionType 字段，切换到具体施工类型时归零
  const costBreakdown = {
    labor: stats?.workTime?.totalCost || 0,
    material: stats?.material?.totalCost || 0,
    equipment: stats?.equipment?.totalCost || 0,
    subcontract: stats?.subcontract?.totalCost || 0,
    coordination: constructionType ? 0 : (stats?.coordination?.totalCost || 0),
  };
  // filteredCost 使用各成本项求和，而非 summary.totalProjectCost
  // 原因：协调费用没有 constructionType 字段，summary.totalProjectCost 中协调费用是全量
  // 所以这里手动排除或置零协调费用，确保 filteredCost 与 costBreakdown 一致
  const filteredCost = constructionType
    ? (stats?.workTime?.totalCost || 0) + (stats?.material?.totalCost || 0)
        + (stats?.equipment?.totalCost || 0) + (stats?.subcontract?.totalCost || 0)
    : (stats?.summary?.totalProjectCost || 0);
  // unfilteredCost 同样需要排除协调费用（后端 unfilteredTotalProjectCost 包含全量协调费用）
  const unfilteredCoordination = stats?.coordination?.totalCost || 0;
  const unfilteredCost = constructionType
    ? ((stats?.summary?.unfilteredTotalProjectCost || 0) - unfilteredCoordination)
    : (stats?.summary?.unfilteredTotalProjectCost || filteredCost);
  const costRatio = unfilteredCost > 0 ? filteredCost / unfilteredCost : 1;

  const totalCost = filteredCost;

  // 精确增项收入：
  // - 全部：手动录入值 + 模块全量汇总
  // - 园林/土建/外墙：仅按 category 过滤的模块汇总值，不含手动录入
  const manualChangeOrder = project.manualChangeOrder ?? 0;
  const additionModuleTotal = project.additionModuleTotal ?? 0;
  const filteredAdditionIncome = stats?.summary?.filteredAdditionIncome ?? 0;
  const effectiveChangeOrder = constructionType
    ? filteredAdditionIncome
    : (manualChangeOrder + additionModuleTotal);

  // 精确合同收入（与增项收入相同的口径）：
  // - 全部：手动录入值 + 模块全量汇总
  // - 园林/土建/外墙：仅按 constructionType 过滤的模块汇总值，不含手动录入
  const manualContract = project.manualContract ?? 0;
  const contractModuleTotal = project.contractModuleTotal ?? 0;
  const filteredContractIncome = stats?.summary?.filteredContractIncome ?? 0;
  const effectiveContract = constructionType
    ? filteredContractIncome
    : (manualContract + contractModuleTotal);

  // 推算预算（当选择了施工类型时，预算按成本比例推算）
  const effectiveBudget = constructionType
    ? (project.budget || 0) * costRatio
    : (project.budget || 0);
  // 收入合计：合同/增项使用精确值，签证/结算/其他按比例推算
  const effectiveRevenue = constructionType
    ? effectiveContract + effectiveChangeOrder
        + ((project.revenueBreakdown?.claim || 0) * costRatio)
        + ((project.revenueBreakdown?.settlement || 0) * costRatio)
        + ((project.revenueBreakdown?.other || 0) * costRatio)
    : (project.revenueTotal || 0);
  const effectiveRevenueBreakdown = constructionType
    ? {
        contract: effectiveContract,
        changeOrder: effectiveChangeOrder,
        claim: (project.revenueBreakdown?.claim || 0) * costRatio,
        settlement: (project.revenueBreakdown?.settlement || 0) * costRatio,
        other: (project.revenueBreakdown?.other || 0) * costRatio,
      }
    : (project.revenueBreakdown || { contract: 0, changeOrder: 0, claim: 0, settlement: 0, other: 0 });

  const estimatedProfit = effectiveRevenue - totalCost;
  const budgetExecutionRate = effectiveBudget > 0 ? (totalCost / effectiveBudget) * 100 : 0;

  // Build top costs
  const topCosts = Object.entries(costBreakdown)
    .map(([category, amount]) => ({
      name: { labor: '人工工时', material: '材料采购', subcontract: '分包工程款', equipment: '设备租赁', coordination: '协调费用' }[category] || category,
      amount,
      pct: totalCost > 0 ? (amount / totalCost) * 100 : 0,
      category,
    }))
    .filter(c => c.amount >= 0)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  // Build alerts
  const health = calculateBudgetHealth(effectiveBudget, totalCost);
  const alertList: Array<{ id: string; type: 'danger' | 'warning' | 'info' | 'success'; title: string; desc: string; action?: string }> = [];

  // health 由 effectiveBudget 计算，effectBudget 为 0 时 health.status 为 'unset'
  // 但若选了施工类型则不显示"预算未配置"预警（项目有预算，只是该类型暂无数据）
  if (health.status === 'unset' && !constructionType) {
    alertList.push({ id: 'a1', type: 'warning' as const, title: '预算未配置', desc: '当前无法计算预算执行率，请在项目设置中配置预算以启用成本监控', action: '去配置' });
  }
  if (contractSummary && contractSummary.contractCount > 0 && effectiveRevenue === 0) {
    alertList.push({ id: 'a2', type: 'info' as const, title: '合同数据未同步', desc: `合同管理模块中有 ${contractSummary.contractCount} 份合同（${fmt(contractSummary.totalAmount, true)}），但收入数据未关联至项目`, action: '去同步' });
  }
  if (health.status === 'over') {
    alertList.push({ id: 'a3', type: 'danger' as const, title: '⚠️ 预算已超支', desc: health.message, action: '查看明细' });
  } else if (health.status === 'warning' || health.status === 'danger') {
    alertList.push({ id: 'a4', type: 'warning' as const, title: `⚠️ 预算${health.label}`, desc: health.message });
  }
  if (project.budgetVersion) {
    alertList.push({ id: 'a5', type: 'info' as const, title: `📌 预算版本 V${project.budgetVersion}`, desc: project.budgetLocked ? '预算已锁定，如需调整请先解锁' : '预算未锁定，可以进行调整' });
  }

  const handleLockBudget = async () => {
    setLockLoading(true);
    try {
      const res = await lockBudget(project.id);
      if (res.success) {
        onBudgetRefresh?.();
      }
    } catch { } finally { setLockLoading(false); }
  };

  const handleUnlockBudget = async () => {
    if (!unlockReason.trim()) return;
    setLockLoading(true);
    try {
      const res = await unlockBudget(project.id, unlockReason);
      if (res.success) {
        setUnlockModalVisible(false);
        setUnlockReason('');
        onBudgetRefresh?.();
      }
    } catch { } finally { setLockLoading(false); }
  };

  return (
    <div style={{
      ...tokens,
      fontFamily: '"Noto Sans SC", -apple-system, BlinkMacSystemFont, sans-serif',
      background: 'transparent',
      color: 'var(--text-primary)',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Noto+Sans+SC:wght@400;500;700&display=swap');
        * { box-sizing: border-box; }
        div.ant-modal-body { padding-top: 12px !important; }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes rollerOpen {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 800px; }
        }
        .anim-1 { animation: fadeInUp 0.35s ease-out 0.05s both; }
        .anim-2 { animation: fadeInUp 0.35s ease-out 0.10s both; }
        .anim-3 { animation: fadeInUp 0.35s ease-out 0.15s both; }
        .anim-4 { animation: fadeInUp 0.35s ease-out 0.20s both; }
        .anim-5 { animation: fadeInUp 0.35s ease-out 0.25s both; }
        .anim-6 { animation: fadeInUp 0.35s ease-out 0.30s both; }
        .anim-7 { animation: fadeInUp 0.35s ease-out 0.35s both; }
        .anim-8 { animation: fadeInUp 0.35s ease-out 0.40s both; }
        .anim-9 { animation: fadeInUp 0.35s ease-out 0.45s both; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-thumb { background: var(--bg-surface); border-radius: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {/* ===== CONSTRUCTION TYPE FILTER (提升到顶部，作为整个卡片统一过滤入口) ===== */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        marginBottom: 12,
      }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
          施工类型：
        </span>
        <Radio.Group
          value={constructionType}
          onChange={e => onConstructionTypeChange?.(e.target.value)}
          size="small"
          optionType="button"
          buttonStyle="solid"
          options={[
            { label: '全部', value: undefined },
            { label: '园林', value: '园林' },
            { label: '土建', value: '土建' },
            { label: '外墙', value: '外墙' },
          ]}
        />
        {constructionType && (
          <span style={{
            fontSize: 11, color: 'var(--accent-blue)',
            background: 'var(--accent-blue-soft)',
            padding: '2px 8px', borderRadius: 4,
          }}>
            当前显示「{constructionType}」统计数据（预算/收入为推算值）
          </span>
        )}
      </div>

      {/* ===== HERO METRICS ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        <div className="anim-1">
          <HeroCard label="预算总额" value={effectiveBudget} variant="budget" onClick={onSetupBudget} cta="配置预算 →" />
        </div>
        <div className="anim-2">
          <HeroCard label="累计成本" value={totalCost} variant="cost" onClick={() => setExpandedCostType(prev => prev === 'worktime' ? null : 'worktime')} />
        </div>
        <div className="anim-3">
          <HeroCard label="累计收入" value={effectiveRevenue} variant="revenue" onClick={() => onViewRevenueDetail?.('contract')} />
        </div>
        <div className="anim-4">
          <HeroCard label="预估利润" value={estimatedProfit} variant={estimatedProfit >= 0 ? 'profit' : 'budget'} />
        </div>
      </div>

      {stats?.selfOperatedSummary && (
        <div className="anim-5">
          <SelfOperatedSummaryPanel summary={stats.selfOperatedSummary} />
        </div>
      )}

      {/* ===== QUICK ACTIONS ===== */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 12,
        padding: '12px 16px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        alignItems: 'center',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginRight: 4 }}>
          快捷操作
        </span>
        <button
          onClick={onAddAddition}
          style={{
            background: 'var(--accent-mint)',
            color: '#fff',
            border: 'none',
            padding: '6px 16px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 4px 12px rgba(93, 138, 127, 0.24)',
          }}
        >
          + 新增进项
        </button>
        <button
          onClick={onAddRectification}
          style={{
            background: 'var(--accent-amber)',
            color: '#fff',
            border: 'none',
            padding: '6px 16px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 4px 12px rgba(217, 168, 107, 0.24)',
          }}
        >
          + 发起整改
        </button>
      </div>

      {/* ===== ROW 1: Execution + Revenue Ring ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        {/* Execution Ring */}
        <div className="anim-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
              📊 预算执行监控
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge status={health.status} />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {effectiveBudget > 0 ? `${budgetExecutionRate.toFixed(1)}% 执行中` : '待配置'}
              </span>
            </div>
          </div>
          <ExecutionRing budget={effectiveBudget} actualCost={totalCost} breakdown={costBreakdown} />
        </div>

        {/* Revenue Ring */}
        <div className="anim-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
              💵 收入来源监控
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-surface)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>
              {Object.values(project.revenueBreakdown || {}).filter(v => v > 0).length} 项来源
            </span>
          </div>
          <RevenueRing
            revenue={effectiveRevenueBreakdown}
            total={effectiveRevenue}
          />
        </div>
      </div>

      {/* ===== ROW 2: Cost + Revenue Breakdown ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Cost Breakdown */}
        <div className="anim-7" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
              💰 成本构成 TOP 5
            </span>
          </div>
          <CostList
            items={topCosts}
            expandedType={expandedCostType}
            projectId={project.id}
            isMobile={isMobile}
            constructionType={constructionType}
            onConstructionTypeChange={onConstructionTypeChange}
            onToggle={(type) => setExpandedCostType(prev => prev === type ? null : type)}
            onClose={() => setExpandedCostType(null)}
          />
        </div>

        {/* Revenue Breakdown */}
        <div className="anim-8" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
              💵 收入构成
            </span>
            {contractSummary && contractSummary.contractCount > 0 && (
              <button
                onClick={() => window.location.href = `/project-contract-management?projectId=${project.id}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--accent-purple)', fontWeight: 600 }}
              >
                合同管理 →
              </button>
            )}
          </div>
          <RevenueList
            revenue={effectiveRevenueBreakdown}
            total={effectiveRevenue}
            onItemClick={(category) => setExpandedRevenueType(prev => prev === category ? null : category as RevenueDetailType)}
          />
          {expandedRevenueType && (
            <div style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              marginTop: 8,
              overflow: 'hidden',
            }}>
              <RevenueDetailPanel
                projectId={project.id}
                category={expandedRevenueType}
                isMobile={isMobile}
                active
                constructionType={constructionType}
              />
            </div>
          )}
        </div>
      </div>


      {/* ===== ROW 3: Alerts (full width) ===== */}
      <div className="anim-9" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16, marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            预警中心
            {alertList.filter(a => a.type === 'danger' || a.type === 'warning').length > 0 && (
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: 'var(--accent-amber)', background: 'var(--accent-amber-soft)', padding: '1px 6px', borderRadius: 10, border: '1px solid rgba(246,196,83,0.2)' }}>
                {alertList.length} 项
              </span>
            )}
          </span>
        </div>
        <AlertSection alerts={alertList} onAlertAction={id => {
          if (id === 'a1') onSetupBudget?.();
        }} />
      </div>

      {/* Unlock Modal */}
      <BudgetHistoryModal
        visible={unlockModalVisible}
        projectId={project.id}
        projectName={project.name}
        onClose={() => setUnlockModalVisible(false)}
      />
    </div>
  );
};

export default BudgetOverviewTower;
